import i18next from 'i18next';

import {
  SET_MANUAL_CAMPAIGNS,
  SET_COUPON_CAMPAIGNS,
  SET_COUPON_RULES,
} from 'constants/CampaignsDB';
import {
  getManualPromotions,
  getCouponCampaigns,
  getCoupons,
  saveIssuedCoupon,
} from 'services/ErplyAPI/campaigns';
import { modalPages as mp } from 'constants/modalPage';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import { timestampInSeconds } from 'utils';
import { getApplicableCoupons } from 'reducers/CampaignsDB';

import { addError, addWarning, dismissAll } from './Error';
import { openModalPage } from './modalPage';
import { createConfirmation } from './Confirmation';
import { prepareCouponData } from './integrations/printer/preparePrintoutData';
import { printCoupon } from './integrations/printer';

/** Fetch coupons/campaigns from Erply API and save them in redux */
export const fetchCampaignsAndCoupons = () => async (dispatch, getState) => {
  const warehouseID = getSelectedWarehouseID(getState());
  await Promise.all([
    getManualPromotions(warehouseID)
      .then(data => dispatch({ type: SET_MANUAL_CAMPAIGNS, payload: { data } }))
      .catch(() => {}), // TODO: Is this safe to ignore?
    getCouponCampaigns(warehouseID)
      .then(data => dispatch({ type: SET_COUPON_CAMPAIGNS, payload: { data } }))
      .catch(() => {}), // TODO: Is this safe to ignore?
    getCoupons()
      .then(data => dispatch({ type: SET_COUPON_RULES, payload: { data } }))
      .catch(() => {}), // TODO: Is this safe to ignore?
  ]);
};

/** Fetch available coupons and then open the coupon view */
export const fetchAvailableCoupons = () => async dispatch => {
  dispatch(
    addWarning(i18next.t('alerts:loading.coupons'), { dismissible: false }),
  );
  try {
    const coupons = await getCoupons().then(records =>
      records.filter(r => r.printedAutomaticallyInPOS === 0),
    );
    dispatch(dismissAll());
    dispatch(openModalPage({ component: mp.coupons, props: { coupons } }));
  } catch (err) {
    dispatch(dismissAll());
    console.error('Unable to fetch available coupons', err);
    addError(i18next.t('alerts:genericError'), {
      selfDismiss: true,
      dismissible: false,
    });
  }
};

export function generateIssuedCouponRequests({ invoiceID, salesDocument }) {
  return async (dispatch, getState) => {
    const {
      warehouseID,
      pointOfSaleID,
      employeeID,
      customerID,
    } = salesDocument;
    const applicableCoupons = getApplicableCoupons(getState());

    return applicableCoupons.map(({ couponID }) => {
      const issuedTimestamp = timestampInSeconds();
      return {
        requestName: 'saveIssuedCoupon',
        customerID,
        couponID,
        warehouseID,
        pointOfSaleID,
        employeeID,
        timestamp: issuedTimestamp,
        invoiceID,
      };
    });
  };
}

function handleCouponPrint({ uniqueIdentifier, couponID }) {
  return async dispatch => {
    try {
      const data = await dispatch(
        prepareCouponData({
          uniqueIdentifier,
          couponID: Number(couponID),
        }),
      );
      try {
        await dispatch(printCoupon(data));
      } catch (error) {
        throw new Error(
          'Failed to print coupon after retrieving printout data.',
          { cause: error },
        );
      }
    } catch (error) {
      throw new Error('Failed to prepare coupon printout data', {
        cause: error,
      });
    }
  };
}

function mapResponsesToCoupon(coupon, responses) {
  const { couponID, promptCashier } = coupon;

  const couponResponse = responses.find(
    response => Number(response.records[0].couponID) === Number(couponID),
  );

  if (couponResponse.status.errorCode !== 0) {
    if (couponResponse.status.errorCode === 1042) {
      /**
       * Sometimes presumed amount of customer's reward points is not enough to issue
       * calculated amount of coupons (API call fails with error code 1042).
       * In that case just skip those coupons since technically they did not satisfy
       * requirements of the coupon rule and therefore should not be issued.
       */
      return null;
    }
    // Do something else? (By the time this action is called sale is saved)
    return null;
  }
  if (!couponResponse.records[0]) {
    return null;
  }
  const { uniqueIdentifier, couponName } = couponResponse.records[0];
  return { uniqueIdentifier, couponID, couponName, promptCashier };
}

export function printIssuedCoupons(couponResponses) {
  return async (dispatch, getState) => {
    const applicableCoupons = getApplicableCoupons(getState());

    const allApplicableCoupons = applicableCoupons
      .map(coupon => mapResponsesToCoupon(coupon, couponResponses))
      .filter(coupon => coupon);

    const automaticallyPrintedCoupons = allApplicableCoupons.filter(
      ({ promptCashier }) => !promptCashier,
    );

    let isConfirmed = true;

    const couponsWithPrompt =
      allApplicableCoupons.length - automaticallyPrintedCoupons.length;
    if (couponsWithPrompt > 0) {
      // Ask once to decide about all coupons that prompt for print
      isConfirmed = await new Promise((resolve, reject) => {
        dispatch(
          createConfirmation(resolve, reject, {
            title: i18next.t('alerts:coupons.title'),
            body: i18next.t('alerts:coupons.body', {
              coupons: couponsWithPrompt,
              automaticallyPrintedCoupons: automaticallyPrintedCoupons.length,
            }),
          }),
        );
      }).catch(() => false);
    }
    const couponsToPrint = isConfirmed
      ? allApplicableCoupons
      : automaticallyPrintedCoupons;

    const failedCouponPrints = (
      await Promise.all(
        couponsToPrint.map(coupon =>
          dispatch(handleCouponPrint(coupon))
            .then(() => null)
            .catch(() => coupon.couponName),
        ),
      )
    ).filter(Boolean);

    if (failedCouponPrints.length) {
      await dispatch(
        addError(
          'Failed to print the following coupons:\n',
          failedCouponPrints.join(','),
        ),
      );
    }
  };
}
